import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Tooltip from "@mui/material/Tooltip"
import axios from "axios";
import { BACKEND_BASE_URL } from "../../config";
import ClearIcon from "@mui/icons-material/Clear"; // Import Clear icon
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";

const Dictionary = () => {
  const [doubleClick, setDoubleClick] = useState([false])
  const [townKeywords, setTownKeywords] = useState([]);
  const [boardKeywords, setBoardKeywords] = useState([]);
  const [currentTownKeyword, setCurrentTownKeyword] = useState("");  // Separate state for Town Government input
  const [currentBoardKeyword, setCurrentBoardKeyword] = useState("");  // Separate state for Board Of Education input
  const [editingIndex, setEditingIndex] = useState({ type: null, index: null });
  const [editText, setEditText] = useState("");
  const [error, setError] = useState("");

  const containerRef = useRef(null);



  useEffect(() => {
    // Fetch the keywords from the backend when the component is mounted
    const fetchKeywords = async () => {
      try {
        const response = await axios.get(`${BACKEND_BASE_URL}/api/keywords/`);

        const { keywords } = response.data;

        // Split keywords into different types based on 'type' field
        const townKeywords = keywords.filter((keyword) => keyword.type === "Town Government");
        const boardKeywords = keywords.filter((keyword) => keyword.type === "Board Of Education");

        // Set the state with the fetched keywords
        setTownKeywords(townKeywords.map((item) => item.keyword));
        setBoardKeywords(boardKeywords.map((item) => item.keyword));
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch keywords.");
      }
    };

    fetchKeywords();
  }, []);

  const handleAddKeyword = async (type) => {
    const keyword = type === "Town Government" ? currentTownKeyword : currentBoardKeyword;
    if (keyword.trim() === "") {
      setError("Keyword cannot be empty.");
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_BASE_URL}/api/keywords/`, {
        keyword,
        type,
      });

      if (type === "Town Government") {
        setTownKeywords((prev) => [...prev, response.data.keyword]);
      } else if (type === "Board Of Education") {
        setBoardKeywords((prev) => [...prev, response.data.keyword]);
      }

      // Reset the appropriate input field
      if (type === "Town Government") {
        setCurrentTownKeyword("");
      } else {
        setCurrentBoardKeyword("");
      }
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add keyword.");
    }
  };

  const handleDeleteKeyword = async (type, keywordToDelete) => {
    try {
      await axios.delete(`${BACKEND_BASE_URL}/api/keywords/`, {
        data: { keyword: keywordToDelete, type },
      });

      if (type === "Town Government") {
        setTownKeywords((prev) => prev.filter((keyword) => keyword !== keywordToDelete));
      } else if (type === "Board Of Education") {
        setBoardKeywords((prev) => prev.filter((keyword) => keyword !== keywordToDelete));
      }
      // setDoubleClick(false)
      // Reset the editing state after deletion to close the TextField
      setEditingIndex({ type: null, index: null });
      setEditText(""); // Clear the edit text
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete keyword.");
    }
  };

  const handleEditClick = (type, index, keyword) => {
    // setDoubleClick(true)
    setEditingIndex({ type, index });
    setEditText(keyword);
  };

  const handleSaveClick = async () => {
    const { type, index } = editingIndex;
    const keywords = type === "Town Government" ? townKeywords : boardKeywords;

    try {
      await axios.put(`${BACKEND_BASE_URL}/api/keywords/`, {
        oldKeyword: keywords[index],
        newKeyword: editText,
        type,
      });

      const updatedKeywords = [...keywords];
      updatedKeywords[index] = editText;

      if (type === "Town Government") {
        setTownKeywords(updatedKeywords);
      } else if (type === "Board Of Education") {
        setBoardKeywords(updatedKeywords);
      }
      // setDoubleClick(false)

      setEditingIndex({ type: null, index: null });
      setEditText("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update keyword.");
    }
  };

  const handleClickOutside = (e) => {
   
    if (containerRef.current && !containerRef.current.contains(e.target)) {

      setEditText("");
      setEditingIndex({ type: null, index: null });
      // setDoubleClick(false);
    }

  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);




  const renderKeywordList = (type, keywords, setKeywords, currentKeyword, setCurrentKeyword) => {
    // Break keywords into chunks of 5
    const keywordChunks = [];
    for (let i = 0; i < keywords.length; i += 5) {
      keywordChunks.push(keywords.slice(i, i + 5));
    }

    const handleKeyDown = (e, type, index) => {
      if (e.key === "Enter") {
        e.preventDefault(); // Prevent form submission or other default behavior on Enter key
        handleSaveClick(); // Trigger the save action
      }
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", position: "relative" }}>
        {/* Lists Section */}
        <Box sx={{ display: "flex", gap: "30px", flexWrap: "wrap", width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "8px", minHeight: "100px" }}>
          {keywordChunks.map((chunk, chunkIndex) => (
            <List key={chunkIndex}>
              {chunk.map((keyword, index) => {
                const absoluteIndex = chunkIndex * 5 + index; // Calculate the absolute index
                return (
                  <ListItem
                    key={absoluteIndex}
                    sx={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
                  >
                    {editingIndex.type === type && editingIndex.index === absoluteIndex ? (
                      <Box sx={{ position: "relative", flex: 1 }} ref={containerRef}>
                        <TextField
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ flex: 1 }}
                          // autoFocus // Automatically focus the input field in edit mode
                          onKeyDown={(e) => handleKeyDown(e, type, absoluteIndex)} // Handle Enter key press
                        />
                        {/* Cross Icon for clearing the TextField */}
                        <IconButton
                          onClick={() => handleDeleteKeyword(type, keyword)}

                          size="small"
                          sx={{
                            color: "currentColor",
                            position: "absolute",
                            top: "-15px", // Adjust based on your layout to fit within TextField's top-right
                            right: "-15px", // Adjust position outside padding of TextField
                            // zIndex: 1, // Ensure it appears above other elements
                          }}
                        >
                          <CancelIcon />

                        </IconButton>
                        <Tooltip title="Press Enter to save">
                          <IconButton

                            color="black"
                            size="small"
                            sx={{
                              opacity:0.5,
                              position: "absolute",
                              top: "10px", // Align at the top
                              right: "0px", // Position at the far right
                              zIndex: 1,
                            }}
                          >
                            <KeyboardReturnIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <ListItemText

                        primary={keyword}
                        sx={{ flex: 1, cursor: "pointer" }}
                        onDoubleClick={() => handleEditClick(type, absoluteIndex, keyword)} // Enable edit on double-click
                      />
                    )}
                  </ListItem>
                );
              })}


            </List>


          ))}

          {/* Tooltip for double-clicking to edit */}
          <Typography
            sx={{ position: "absolute", right: "10px", fontSize: "12px", color: "#888" }}
          >
            Double-click on a keyword to edit
          </Typography>

        </Box>

        {/* Tooltip for double-clicking to edit */}


        {/* Add Keyword Section */}
        <Box sx={{ display: "flex", gap: "10px", marginTop: "10px" }}>
          <TextField
            value={currentKeyword}
            onChange={(e) => setCurrentKeyword(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Enter a keyword"
            sx={{ flex: 1, minWidth: "70%" }}
          />
          <Button
            onClick={() => handleAddKeyword(type)}
            variant="contained"
            color="primary"
            size="large"
            sx={{ minWidth: "150px" }}
          >
            Add Keyword
          </Button>
        </Box>


      </Box>
    );
  };


  return (
    <Grid container spacing={4} >
      <Grid item xs={12}>
        <Typography style={{ fontWeight: "bold" }} variant="h4">
          Manage Dictionary
        </Typography>

        {/* Town Government Section */}
        <Typography variant="h6">Town Government Dictionary</Typography>
        {renderKeywordList("Town Government", townKeywords, setTownKeywords, currentTownKeyword, setCurrentTownKeyword)}

        {/* Board of Education Section */}
        <Typography variant="h6" sx={{ marginTop: "40px" }}>
          Board of Education Dictionary
        </Typography>
        {renderKeywordList("Board Of Education", boardKeywords, setBoardKeywords, currentBoardKeyword, setCurrentBoardKeyword)}
      </Grid>
    </Grid>
  );
};

export default Dictionary;
